import { Container, Grid } from '@ingeniorforeningen/figurine-core';
import { IApiContentResponseModel } from '@ingeniorforeningen/umbraco-client';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import Layout from '@/layouts/Layout';
import { getPageData } from '../lib/apiHelpers';

type TSequentialScriptLoaderProps = {
  src: string;
  dependencyScript: string;
};

const SequentialScriptLoader = ({ src, dependencyScript }: TSequentialScriptLoaderProps) => {
  const [firstScriptLoaded, setFirstScriptLoaded] = useState(false);

  useEffect(() => {
    const firstScript = document.createElement('script');
    firstScript.src = dependencyScript;
    firstScript.onload = () => {
      setFirstScriptLoaded(true);
    };
    document.head.appendChild(firstScript);

    return () => {
      document.head.removeChild(firstScript);
    };
  }, [dependencyScript]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (firstScriptLoaded) {
      const secondScript = document.createElement('script');
      secondScript.src = src;
      document.head.appendChild(secondScript);

      return () => {
        document.head.removeChild(secondScript);
      };
    }
  }, [src, firstScriptLoaded]);

  return null;
};

const Soeg = ({
  data: { page, settings },
}: {
  data: {
    page: IApiContentResponseModel;
    settings: IApiContentResponseModel;
  };
}) => {
  const { locale } = useRouter();

  return (
    <Layout page={page} settings={settings}>
      <SequentialScriptLoader
        dependencyScript="https://customer.cludo.com/scripts/bundles/search-script.js"
        src="https://customer.cludo.com/assets/2677/13262/cludo-helper.js"
      />
      <Grid container>
        <Grid item justify="center">
          {/*  If the container should match the blocks width, then change Container to BlockContainer */}
          <Container>
            <div id="cludo-search-results" className="cludo-search-results">
              <div className="cludo-input-form" role="search" id="cludo-search-input">
                <input
                  className="cludo-input-form__input"
                  title={locale === 'da' ? 'Søg på siden' : 'Search the page'}
                  name="searchrequest"
                  type="search"
                  autoComplete="off"
                  placeholder={locale === 'da' ? 'Skriv dit søgeord' : 'Type your search'}
                  aria-autocomplete="list"
                  aria-haspopup="true"
                />
                <button
                  type="submit"
                  title="Search"
                  className="cludo-input-form__search-button fa-solid fa-magnifying-glass"
                  aria-label="search"
                />
              </div>

              <div className="cludo-search-results__layout">
                <div
                  className="cludo-search-results__facets search-filters"
                  aria-controls="search-results"
                />
                <div className="cludo-search-results__results-wrapper">
                  <div
                    className="cludo-search-results__search-result-count search-result-count"
                    role="status"
                  />
                </div>
              </div>
            </div>

            <Script
              id="search_script"
              dangerouslySetInnerHTML={{
                __html: `
              var cludo_engineId = ${locale === 'da' ? 13262 : 13330};
              var cludo_language = '${locale}';
              var cludo_searchUrl = '${locale === 'da' ? '/soeg' : '/search'}';
              `,
              }}
            />

            <Head>
              <link
                rel="stylesheet"
                href="https://customer.cludo.com/assets/2677/13262/cludo-search.min.css"
                type="text/css"
              />
            </Head>
          </Container>
        </Grid>
      </Grid>
    </Layout>
  );
};

export async function getStaticProps({ preview, locale }: { preview: boolean; locale: string }) {
  const path = '/soeg';
  const umbracoData = await getPageData({ path, locale, preview });

  return {
    props: {
      data: umbracoData,
      preview: preview || null,
    },
    revalidate: 600,
    notFound: !umbracoData.page,
  };
}

export default Soeg;
